import { Box, ClickAwayListener, List } from '@mui/material';
import useIsMobile from 'hooks/useIsMobile';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useProjectId } from 'store/project';
import { useCurrentUser } from 'store/user';
import { FrontVersion } from '../../index';
import { HeaderLogo } from '../NavigationHeaderLogo/HeaderLogo';
import NavigationItemButton from '../NavigationItem/NavigationItemButton';
import useStyles, { drawerCollapsed, drawerExpanded } from './styles';
import { NavigationItemProps } from './types';
import { useDeviceType } from './useDeviceType';
import { useTemplateIdFromInspection } from 'store/inspection/hooks';
import {
  buildNavigationItems,
  getNavigationDrawerState,
} from './model';

export const NAVIGATION_DRAWER_ZINDEX = 1200;

function ReactNavigationItem({
  navigationItemProps,
}: {
  navigationItemProps: NavigationItemProps;
}): ReactElement {
  const {
    visible,
    isSubItem,
    componentKey,
    redirectAddress,
    textId,
    Icon,
    isNavigationExpanded,
    disabled,
    setCollapsed,
  } = navigationItemProps;
  const navigate = useNavigate();
  const { isTouch } = useDeviceType();
  const handleTouch = useCallback(
    (e: TouchEvent): void => {
      if (disabled) return;

      setCollapsed();
    },
    [disabled, isNavigationExpanded, setCollapsed]
  );

  const navItemRef = useRef<any>(null);

  const handleInteraction = useCallback(
    (e) => {
      if (disabled) return;
      if (redirectAddress) {
        navigate(redirectAddress, {
          state: navigationItemProps.navigationState,
        });
      }
      setCollapsed();
    },
    [disabled, redirectAddress, navigationItemProps.navigationState, setCollapsed]
  );

  if (redirectAddress) {
    return (
      <Box
        onTouchEnd={handleInteraction}
        onClick={handleInteraction}
        ref={navItemRef}
        // to={redirectAddress}
        sx={{
          display: visible ? 'block' : 'none',
          textDecoration: 'none',
          opacity: disabled ? 0.4 : 1,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
        data-qa={`navigation-item-${componentKey}`}
        // onMouseMove={(e): void => {
        //   // fires after touch start which causes expanding drawer by parent
        //   e.stopPropagation();
        // }}
        key={componentKey}
      >
        <NavigationItemButton {...navigationItemProps} />
      </Box>
    );
  }

  return (
    <Box
      ref={navItemRef}
      sx={{
        display: visible ? 'block' : 'none',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
      data-qa={`navigation-item-${componentKey}`}
      key={componentKey}
    >
      <NavigationItemButton {...navigationItemProps} />
    </Box>
  );
}

const NavigationDrawerPresentational = ({
  isNavigationExpanded,
  setNavigationExpanded,
}): ReactElement => {
  const { isTouch } = useDeviceType();
  const drawerRef = useRef<HTMLDivElement>(null);

  const setExpanded = useCallback(() => {
    setNavigationExpanded(true);
  }, [setNavigationExpanded]);

  const setCollapsed = useCallback(() => {
    setNavigationExpanded(false);
  }, [setNavigationExpanded]);

  // Obsługa dotyku na całej nawigacji
  const handleDrawerTouch = useCallback(
    (e: TouchEvent) => {
      if (e.target === drawerRef.current) {
        e.preventDefault();
        setNavigationExpanded(!isNavigationExpanded);
      }
    },
    [isNavigationExpanded, setNavigationExpanded]
  );

  const handleLogoTouch = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      e.preventDefault();
      setNavigationExpanded(!isNavigationExpanded);
    },
    [isNavigationExpanded, setNavigationExpanded]
  );

  useEffect(() => {
    const drawer = drawerRef.current;

    if (!drawer || !isTouch) return;

    drawer.addEventListener('touchstart', handleDrawerTouch, {
      passive: false,
    });

    return () => {
      drawer.removeEventListener('touchstart', handleDrawerTouch);
    };
  }, [handleDrawerTouch, isTouch]);

  const isMobile = useIsMobile();
  const classes = useStyles({ isMobile });
  const projectId = useProjectId();
  const currentUserData = useCurrentUser();
  // Get template ID from inspection if we're on an inspection page
  const templateIdFromInspection = useTemplateIdFromInspection();

  const {
    isUserReady,
    isNavigationDisabled,
    permissions,
  } = getNavigationDrawerState(
    currentUserData,
    projectId
  );

  const navigationItems = buildNavigationItems({
    projectId,
    templateIdFromInspection,
    isUserReady,
    isNavigationDisabled,
    isNavigationExpanded,
    setCollapsed,
    permissions,
  });

  return (
    <ClickAwayListener onClickAway={setCollapsed}>
      <Box
        ref={drawerRef}
        onMouseEnter={setExpanded}
        onMouseLeave={setCollapsed}
        id='navigationDrawer'
        sx={{
          zIndex: NAVIGATION_DRAWER_ZINDEX,
          position: 'absolute',
          top: 0,
          height: '100%',
          ...(isNavigationExpanded ? drawerExpanded : drawerCollapsed),
        }}
      >
        <Box onTouchEnd={handleLogoTouch}>
          <HeaderLogo />
        </Box>

        <List className={classes.navigationButtonsWrapper}>
          <Box className='top-buttons'>
            {navigationItems.map((navigationItemProps) => (
              <ReactNavigationItem
                key={navigationItemProps.componentKey}
                navigationItemProps={navigationItemProps}
              />
            ))}
          </Box>
        </List>
        <FrontVersion />
      </Box>
    </ClickAwayListener>
  );
};

export const NavigationDrawer = React.memo(NavigationDrawerPresentational);
